import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material'

import { mutations } from '../../graphql'

const TERMS_VERSION = '1.0'

export function ClaimFundNewUser({ token, claimDetails, onSwitchToExistingUser }) {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: claimDetails.clientEmail,
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  })
  const [errors, setErrors] = useState({})

  const [claimFund, { loading, error }] = useMutation(mutations.fundClaims.claimFundNewUser, {
    onCompleted: () => {
      navigate('/sign-up/success', { state: { otherPendingInvites: claimDetails.otherPendingInvites } })
    },
    onError: (err) => {
      console.error('Claim fund error:', err)
    },
  })

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
    // Clear error when field changes
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the Terms & Conditions'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) return

    await claimFund({
      variables: {
        input: {
          token,
          email: formData.email,
          password: formData.password,
          acceptTerms: formData.acceptTerms,
          termsVersion: TERMS_VERSION,
        },
      },
    })
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Create Your Giving Fund
          </Typography>
        </Box>

        <Box sx={{ mb: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {claimDetails.fundType === 'corporate'
              ? claimDetails.corporateName || claimDetails.fundName
              : `${claimDetails.clientFirstName} ${claimDetails.clientLastName}`}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error.message}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Fund Details
          </Typography>

          <TextField
            fullWidth
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            type="password"
            label="Create Password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password || 'At least 8 characters'}
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            type="password"
            label="Confirm Password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            sx={{ mb: 3 }}
            required
          />

          <FormControlLabel
            control={
              <Checkbox name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} color="primary" />
            }
            label={
              <Typography variant="body2">
                I agree to the{' '}
                <Link
                  href="https://guide.givewise.ca/policy-and-user-documents/terms-and-conditions"
                  target="_blank"
                  rel="noopener"
                >
                  Terms & Conditions
                </Link>{' '}
                and{' '}
                <Link
                  href="https://guide.givewise.ca/policy-and-user-documents/privacy-policy"
                  target="_blank"
                  rel="noopener"
                >
                  Privacy Policy
                </Link>
              </Typography>
            }
          />
          {errors.acceptTerms && (
            <Typography color="error" variant="caption" display="block" sx={{ ml: 4 }}>
              {errors.acceptTerms}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={loading}
            sx={{ mt: 3 }}
          >
            {loading ? 'Creating...' : 'Create Giving Fund'}
          </Button>
        </form>

        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Already have a GiveWise account?
          </Typography>
          <Button variant="outlined" color="primary" size="small" onClick={onSwitchToExistingUser}>
            Sign In
          </Button>
        </Box>
      </Paper>
    </Container>
  )
}
